import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { selectAuth, selectUser } from '../redux/main/authSlice';
import { selectAccountData } from '../redux/main/accountSlice';
import DynamicTitle from '../components/DynamicTitle';
import VehicleSelect from '../components/VehicleSelect';
import { toast } from 'react-toastify';

export default function EmployeeAppointmentDetail() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  
  // Stati per i dati del form
  const [appointment, setAppointment] = useState(null);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [notes, setNotes] = useState('');
  const [isClosed, setIsClosed] = useState(false);
  const [files, setFiles] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false); // Flag per sapere se stiamo aggiornando o creando
  
  // Stati per il caricamento e gli errori
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  
  // Recupero token, user e account dal Redux store
  const auth = useSelector(selectAuth);
  const token = auth?.token;
  const user = useSelector(selectUser);
  const accountData = useSelector(selectAccountData);
  
  // Recupera l'appuntamento e i dati necessari
  useEffect(() => {
    const fetchAppointmentAndVehicles = async () => {
      if (!token || !accountData?._id || !appointmentId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Recupera i dettagli dell'appuntamento
        const appointmentResponse = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/appointments/detail?accountId=${accountData._id}&appointmentId=${appointmentId}`, 
          {
            headers: {
              'Content-Type': 'application/json',
              'auth-token': token
            }
          }
        );
        
        if (!appointmentResponse.ok) {
          throw new Error(`Errore nel recupero dell'appuntamento: ${appointmentResponse.status}`);
        }
        
        const appointmentData = await appointmentResponse.json();
        setAppointment(appointmentData);
        
        // Verifica prima se ci sono dettagli di chiusura con veicoli impostati per l'utente
        let vehicleFound = false;
        
        if (appointmentData.closingDetails && appointmentData.closingDetails.length > 0) {
          const userClosingDetail = appointmentData.closingDetails.find(
            detail => detail.userId === user._id
          );
          
          if (userClosingDetail && userClosingDetail.vehicleId) {
            setSelectedVehicle({ 
              value: userClosingDetail.vehicleId, 
              label: userClosingDetail.vehicleLabel || 'Veicolo selezionato',
              data: { vehicleId: userClosingDetail.vehicleId, vehicleLabel: userClosingDetail.vehicleLabel }
            });
            vehicleFound = true;
          }
        }
        
        // Se non è stato trovato un veicolo nei closingDetails, cerca nei veicoli dell'appuntamento
        if (!vehicleFound && appointmentData.vehicles && appointmentData.vehicles.length > 0) {
          // Cerca un veicolo assegnato a questo utente
          const userVehicleAssignment = appointmentData.users.find(u => u.userId === user._id && u.vehicleId);
          
          if (userVehicleAssignment) {
            // Cerca il veicolo corrispondente nell'array dei veicoli
            const userVehicle = appointmentData.vehicles.find(v => v.vehicleId === userVehicleAssignment.vehicleId);
            
            if (userVehicle) {
              setSelectedVehicle({ 
                value: userVehicle.vehicleId, 
                label: userVehicle.vehicleLabel || 'Veicolo selezionato',
                data: userVehicle
              });
              vehicleFound = true;
            }
          } else {
            // Se non c'è un'assegnazione specifica, prende il primo veicolo dell'appuntamento
            const firstVehicle = appointmentData.vehicles[0];
            if (firstVehicle) {
              setSelectedVehicle({ 
                value: firstVehicle.vehicleId, 
                label: firstVehicle.vehicleLabel || 'Veicolo selezionato',
                data: firstVehicle
              });
            }
          }
        }
        
        // Verifica se l'utente ha già inserito dati di chiusura
        // Verifica se ci sono già dettagli di chiusura per l'utente corrente
        if (appointmentData.closingDetails && appointmentData.closingDetails.length > 0) {
          const userClosingDetail = appointmentData.closingDetails.find(
            detail => detail.userId === user.userId
          );
          
          if (userClosingDetail) {
            // Utente ha già un elemento nei closingDetails - lo stiamo aggiornando
            setIsUpdating(true);
            console.log('Caricando dati esistenti per l\'utente:', user._id);
            
            // Imposta i valori dai dati di chiusura esistenti
            if (userClosingDetail.hours !== undefined) setHours(userClosingDetail.hours);
            if (userClosingDetail.minutes !== undefined) setMinutes(userClosingDetail.minutes);
            if (userClosingDetail.vehicleId) {
              setSelectedVehicle({ 
                value: userClosingDetail.vehicleId, 
                label: userClosingDetail.vehicleLabel || 'Veicolo selezionato',
                data: { vehicleId: userClosingDetail.vehicleId, vehicleLabel: userClosingDetail.vehicleLabel }
              });
            }
            if (userClosingDetail.notes) setNotes(userClosingDetail.notes);
            if (userClosingDetail.files) setFiles(userClosingDetail.files);
            if (userClosingDetail.isClosed !== undefined) setIsClosed(userClosingDetail.isClosed);
          } else {
            // Utente non ha ancora un elemento nei closingDetails - ne creeremo uno nuovo
            setIsUpdating(false);
            console.log('Nessun dato esistente per l\'utente:', user._id, '- Creazione di un nuovo elemento');
          }
        } else {
          // Non ci sono closingDetails nell'appuntamento - creeremo il primo
          setIsUpdating(false);
          console.log('Nessun closingDetail nell\'appuntamento - Creazione del primo elemento');
        }
        
        // Non è più necessario recuperare i veicoli qui
        // Il componente VehicleSelect gestisce internamente il caricamento dei veicoli
      } catch (err) {
        console.error('Errore durante il recupero dei dati:', err);
        setError('Impossibile caricare i dettagli dell\'appuntamento. Riprova più tardi.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAppointmentAndVehicles();
  }, [token, accountData?._id, appointmentId, user?._id]);
  
  // Funzione per la validazione del form
  const validateForm = () => {
    const errors = {};
    
    if (hours < 0) errors.hours = 'Le ore devono essere un numero positivo';
    if (minutes < 0 || minutes > 59) errors.minutes = 'I minuti devono essere tra 0 e 59';
    
    if (isClosed && files.length === 0) {
      errors.files = 'È necessario caricare almeno un file per chiudere l\'attività';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Gestione del caricamento dei file
  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    
    // Verifica se sono già stati caricati file con lo stesso nome
    const fileNames = files.map(f => f.name || f.fileName);
    const duplicates = newFiles.filter(nf => fileNames.includes(nf.name));
    
    if (duplicates.length > 0) {
      toast.warning(`Alcuni file hanno nomi duplicati e non saranno aggiunti: ${duplicates.map(d => d.name).join(', ')}`);
      const uniqueFiles = newFiles.filter(nf => !fileNames.includes(nf.name));
      
      // Aggiungi anteprima per i file nuovi
      const newFilesWithPreview = uniqueFiles.map(file => ({
        file,
        name: file.name,
        preview: URL.createObjectURL(file),
        size: file.size,
        type: file.type,
        isNew: true // Flag per identificare i nuovi file
      }));
      
      setFiles(prevFiles => [...prevFiles, ...newFilesWithPreview]);
    } else {
      // Aggiungi anteprima per i file nuovi
      const newFilesWithPreview = newFiles.map(file => ({
        file,
        name: file.name,
        preview: URL.createObjectURL(file),
        size: file.size,
        type: file.type,
        isNew: true // Flag per identificare i nuovi file
      }));
      
      setFiles(prevFiles => [...prevFiles, ...newFilesWithPreview]);
    }
  };
  
  // Rimuovi un file dalla lista
  const removeFile = (fileName) => {
    const fileToRemove = files.find(f => (f.name || f.fileName) === fileName);
    
    // Se il file ha una preview, revoca l'URL per liberare memoria
    if (fileToRemove && fileToRemove.preview && fileToRemove.isNew) {
      URL.revokeObjectURL(fileToRemove.preview);
    }
    
    setFiles(prevFiles => prevFiles.filter(f => (f.name || f.fileName) !== fileName));
  };
  
  // Apri il file in una nuova tab
  const openFile = (file) => {
    if (file.isNew && file.preview) {
      // Per i file nuovi, apri la preview
      window.open(file.preview, '_blank');
    } else if (file.url) {
      // Per i file già caricati, apri l'URL
      window.open(file.url, '_blank');
    }
  };
  
  // Carica il file su Kubernetes e restituisce l'URL
  const uploadFileToKubernetes = async (fileObj) => {
    // Se è già un URL o ha già un URL, lo restituiamo direttamente
    if (typeof fileObj === 'string') {
      return fileObj;
    }
    if (fileObj.url) {
      return fileObj.url;
    }
    
    // Determiniamo se è un oggetto con file o direttamente un File
    let file;
    
    if (fileObj.file instanceof File) {
      file = fileObj.file;
    } else if (fileObj instanceof File) {
      file = fileObj;
    } else {
      console.warn('Formato file non valido:', fileObj);
      return null;
    }
    
    const fileName = file.name;
    const ext = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
    
    // Verifica dei tipi di file consentiti
    const allowedFileTypes = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'doc', 'docx', 'xls', 'xlsx', 'txt'];
    
    if (!allowedFileTypes.includes(ext)) {
      toast.error(`Tipo di file non consentito: ${ext}`);
      return null;
    }
    
    try {
      // Prepara i dati per il caricamento
      const data = new FormData();
      data.append('file', file, fileName);
      data.append('uploadName', fileName);
      data.append('uploadTitle', fileName);
      data.append('path', `appointments/${accountData._id}/${appointmentId}`);
      
      // Chiamata API per caricare il file
      const apiUrl = `${import.meta.env.VITE_API_BASE_URL}/files/upload`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'auth-token': token
        },
        body: data
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Errore nell'upload del file: ${errorText}`);
      }
      
      const result = await response.json();
      console.log('File caricato con successo:', result);
      return result.url || result.fileUrl || result;
    } catch (error) {
      console.error('Errore durante il caricamento del file:', error);
      toast.error(`Errore nel caricamento del file ${fileName}`);
      throw error;
    }
  };
  
  // Gestione dell'invio del form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Debug: verifica struttura oggetto user
    console.log('User object:', user);
    console.log('User ID:', user._id);
    console.log('User userId:', user.userId);
    
    if (!validateForm()) return;
    
    setSubmitting(true);
    
    try {
      // Prepara i dati per l'invio
      const closingDetail = {
        userId: user.userId,
        userLabel: `${user.name || ''} ${user.surname || ''}`.trim(),
        hours: parseInt(hours),
        minutes: parseInt(minutes),
        vehicleId: selectedVehicle?.value || '',
        vehicleLabel: selectedVehicle?.label || '',
        notes,
        isClosed,
        updateDate: new Date().toISOString()
      };
      
      // Se ci sono file, caricali su Kubernetes e salva gli URL
      if (files.length > 0) {
        // Mostra indicatore di caricamento
        toast.info('Caricamento dei file in corso...');
        
        // Array per tenere traccia di eventuali errori
        const errors = [];
        
        // Carica i file e ottieni gli URL
        const filePromises = files.map(async file => {
          try {
            // Se il file ha già un URL, lo usiamo direttamente
            if (file.url && !file.isNew) {
              return {
                fileName: file.name || file.fileName,
                fileType: file.type || file.fileType,
                fileSize: file.size || file.fileSize,
                url: file.url,
                uploadDate: file.uploadDate || new Date().toISOString()
              };
            }
            
            // Altrimenti è un nuovo file, caricalo su Kubernetes
            const fileUrl = await uploadFileToKubernetes(file);
            if (fileUrl) {
              return {
                fileName: file.name,
                fileType: file.type,
                fileSize: file.size,
                url: fileUrl,
                uploadDate: new Date().toISOString()
              };
            } else {
              errors.push(file.name);
              return null;
            }
          } catch (error) {
            console.error(`Errore durante il caricamento del file ${file.name}:`, error);
            errors.push(file.name);
            return null;
          }
        });
        
        // Attendi che tutti i file siano caricati
        const uploadedFiles = (await Promise.all(filePromises)).filter(Boolean);
        
        // Mostra errori se ci sono stati problemi
        if (errors.length > 0) {
          toast.error(`Errore nel caricamento di ${errors.length} file: ${errors.join(', ')}`);
          if (errors.length === files.length) {
            throw new Error('Nessun file è stato caricato con successo');
          }
        }
        
        // Salva i riferimenti ai file caricati
        closingDetail.files = uploadedFiles;
      }
      
      // Invia i dati all'API
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/appointments/updateClosingDetails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token
        },
        body: JSON.stringify({
          accountId: accountData._id,
          appointmentId,
          closingDetail
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Errore durante il salvataggio: ${response.status}`);
      }
      
      // Mostra un messaggio diverso a seconda se stiamo aggiornando o creando
      if (isUpdating) {
        toast.success('Dettagli aggiornati con successo');
      } else {
        toast.success('Dettagli inseriti con successo');  
      }
      
      // Torna alla pagina principale
      navigate('/employeeJobsList');
    } catch (err) {
      console.error('Errore durante il salvataggio:', err);
      toast.error(err.message || 'Errore durante il salvataggio. Riprova più tardi.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Formatta la data in modo leggibile
  const formatDate = (dateString) => {
    return format(new Date(dateString), 'EEEE d MMMM yyyy', { locale: it });
  };
  
  // Formatta l'ora in modo leggibile
  const formatTime = (dateString) => {
    return format(new Date(dateString), 'HH:mm');
  };
  
  if (loading) {
    return (
      <div className="min-h-screen p-4 flex flex-col justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="mt-4 text-gray-600">Caricamento in corso...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen p-4 flex flex-col justify-center items-center">
        <div className="bg-red-100 text-red-700 p-4 rounded-lg max-w-md w-full">
          <h2 className="text-lg font-semibold mb-2">Errore</h2>
          <p>{error}</p>
          <button 
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            onClick={() => navigate('/employeeJobsList')}
          >
            Torna alla lista appuntamenti
          </button>
        </div>
      </div>
    );
  }
  
  if (!appointment) {
    return (
      <div className="min-h-screen p-4 flex flex-col justify-center items-center">
        <div className="bg-yellow-100 text-yellow-700 p-4 rounded-lg max-w-md w-full">
          <h2 className="text-lg font-semibold mb-2">Appuntamento non trovato</h2>
          <p>L'appuntamento richiesto non è stato trovato o non hai i permessi per visualizzarlo.</p>
          <button 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            onClick={() => navigate('/employeeJobsList')}
          >
            Torna alla lista appuntamenti
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-4 max-w-screen-lg mx-auto">
      <DynamicTitle title={`Dettaglio Appuntamento - ${appointment.title || 'Senza titolo'}`} />
      
      <div className="flex justify-between items-center mb-6">
        <button 
          onClick={() => navigate('/employeeJobsList')} 
          className="text-blue-600 hover:text-blue-800 flex items-center"
        >
          <i className="fi fi-br-angle-left mr-2"></i>
          Torna alla lista
        </button>
      </div>
      
      {/* Dettagli dell'appuntamento */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <h2 className="text-xl font-semibold mb-2">{appointment.title || 'Appuntamento senza titolo'}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="flex items-center">
            <i className="fi fi-br-calendar mr-2 text-gray-500"></i>
            <span>{formatDate(appointment.fromDate)}</span>
          </div>
          
          <div className="flex items-center">
            <i className="fi fi-br-clock mr-2 text-gray-500"></i>
            <span>{formatTime(appointment.fromDate)} - {formatTime(appointment.toDate)}</span>
          </div>
          
          {appointment.systemAddressLabel && (
            <div className="flex items-center">
              <i className="fi fi-br-marker mr-2 text-gray-500"></i>
              <span>{appointment.systemAddressLabel}</span>
            </div>
          )}
          
          {appointment.jobCode && (
            <div className="flex items-center">
              <i className="fi fi-br-briefcase mr-2 text-gray-500"></i>
              <span>Commessa: {appointment.jobCode}</span>
            </div>
          )}
          
          {appointment.jobTypeLabel && (
            <div className="flex items-center">
              <i className="fi fi-br-settings mr-2 text-gray-500"></i>
              <span>Tipo: {appointment.jobTypeLabel}</span>
            </div>
          )}
        </div>
        
        {appointment.notes && (
          <div className="mt-2 p-3 bg-gray-50 rounded text-sm">
            <h3 className="font-medium mb-1">Note:</h3>
            <p>{appointment.notes}</p>
          </div>
        )}
      </div>
      
      {/* Form per l'inserimento dei dettagli di chiusura */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">
            {isUpdating ? 'Modifica dettagli di chiusura attività' : 'Inserisci dettagli di chiusura attività'}
          </h2>
          {isUpdating && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <span className="w-2 h-2 mr-1 rounded-full bg-green-500"></span>
              Ore già caricate
            </span>
          )}
        </div>
        
        {/* Tempo impiegato */}
        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-2">
            Tempo impiegato
          </label>
          <div className="flex items-center space-x-3">
            <div className="flex-1">
              <label className="block text-gray-600 text-sm mb-1">Ore</label>
              <input
                type="number"
                min="0"
                value={hours}
                onChange={(e) => setHours(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {validationErrors.hours && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.hours}</p>
              )}
            </div>
            <div className="flex-1">
              <label className="block text-gray-600 text-sm mb-1">Minuti</label>
              <input
                type="number"
                min="0"
                max="59"
                value={minutes}
                onChange={(e) => setMinutes(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {validationErrors.minutes && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.minutes}</p>
              )}
            </div>
          </div>
        </div>
        
        {/* Selezione veicolo */}
        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-2">
            Veicolo utilizzato
          </label>
          <VehicleSelect
            selectedOptions={selectedVehicle}
            setSelectedOptions={setSelectedVehicle}
            isMulti={false}
            placeholder="Seleziona un veicolo..."
          />
        </div>
        
        {/* Note */}
        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-2">
            Note sull'attività svolta
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows="4"
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Inserisci note sull'intervento effettuato..."
          ></textarea>
        </div>
        
        {/* Caricamento file */}
        <div className="mb-6">
          <label className="block text-gray-700 font-medium mb-2">
            File allegati
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 mb-2">
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              className="w-full"
            />
            <p className="text-sm text-gray-500 mt-1">
              Puoi caricare più file contemporaneamente
            </p>
          </div>
          
          {/* Lista dei file caricati con anteprime */}
          {files.length > 0 && (
            <div className="mt-2">
              <h4 className="text-sm font-medium mb-2">File caricati:</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {files.map((file, index) => {
                  // Determina l'estensione del file per scegliere l'icona appropriata
                  const fileName = file.name || file.fileName || '';
                  const ext = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
                  const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(ext);
                  const isPdf = ext === 'pdf';
                  const isDoc = ['doc', 'docx'].includes(ext);
                  const isExcel = ['xls', 'xlsx'].includes(ext);
                  
                  // Determina la preview (se disponibile) o l'icona appropriata
                  let preview = null;
                  if (isImage && (file.preview || file.url)) {
                    preview = (
                      <div className="h-24 w-full rounded overflow-hidden bg-gray-100 flex items-center justify-center">
                        <img 
                          src={file.preview || file.url} 
                          alt={fileName} 
                          className="h-full w-full object-contain"
                        />
                      </div>
                    );
                  } else {
                    let iconClass = "fi fi-br-file text-3xl";
                    if (isPdf) iconClass = "fi fi-br-document-alt text-3xl text-red-500";
                    else if (isDoc) iconClass = "fi fi-br-document text-3xl text-blue-500";
                    else if (isExcel) iconClass = "fi fi-br-file-spreadsheet text-3xl text-green-500";
                    
                    preview = (
                      <div className="h-24 w-full rounded overflow-hidden bg-gray-100 flex items-center justify-center">
                        <i className={iconClass}></i>
                      </div>
                    );
                  }
                  
                  return (
                    <div key={index} className="border rounded-lg overflow-hidden bg-white shadow-sm hover:shadow transition-shadow duration-200">
                      {/* Anteprima cliccabile */}
                      <div className="cursor-pointer" onClick={() => openFile(file)}>
                        {preview}
                      </div>
                      
                      {/* Nome file e pulsante rimuovi */}
                      <div className="p-2 flex items-center justify-between">
                        <div 
                          className="text-sm truncate max-w-full text-blue-600 hover:text-blue-800 cursor-pointer" 
                          onClick={() => openFile(file)}
                          title={fileName}
                        >
                          {fileName}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile(fileName);
                          }}
                          className="text-red-500 hover:text-red-700 ml-2 p-1"
                          title="Rimuovi file"
                        >
                          <i className="fi fi-br-cross-small"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {validationErrors.files && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.files}</p>
          )}
        </div>
        
        {/* Checkbox per chiusura attività */}
        <div className="mb-6">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={isClosed}
              onChange={(e) => setIsClosed(e.target.checked)}
              className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="ml-2 text-gray-700">Contrassegna l'attività come completata</span>
          </label>
          <p className="text-sm text-gray-500 mt-1">
            Se contrassegni l'attività come completata, devi caricare almeno un file
          </p>
        </div>
        
        {/* Pulsanti di azione */}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={() => navigate('/employeeJobsList')}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
            disabled={submitting}
          >
            Annulla
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="animate-spin h-4 w-4 mr-2 border-2 border-white border-r-transparent rounded-full"></span>
                Salvataggio...
              </>
            ) : (
              'Salva'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
